import React, { useMemo, useState } from 'react';
import { history } from '@umijs/max';
import { Popover } from 'antd';
import { DownOutlined, PlusOutlined } from '@ant-design/icons';
import insightIcon from '@/assets/insight-chip.png';
import seoBlogIcon from '@/assets/seo-blog-chip.png';
import museIcon from '@/assets/muse-logo-chip.png';
import imageIcon from '@/assets/image.png';
import reelIcon from '@/assets/reel.png';
import './index.less';

export interface SidebarCreateEntryProps {
  collapsed?: boolean;
}

interface CreateProductOption {
  key: string;
  label: string;
  path?: string;
  iconSrc: string;
  children?: Array<{
    key: string;
    label: string;
    path: string;
    iconSrc: string;
  }>;
}

const CREATE_PRODUCT_OPTIONS: CreateProductOption[] = [
  {
    key: 'insight',
    label: 'Insight',
    path: '/ai-analysis',
    iconSrc: insightIcon,
  },
  {
    key: 'seo',
    label: 'SEO Blog',
    path: '/seo-create/blog-creation',
    iconSrc: seoBlogIcon,
  },
  {
    key: 'muse',
    label: 'Muse',
    iconSrc: museIcon,
    children: [
      {
        key: 'image',
        label: 'Image',
        path: '/generate-media/chat-image',
        iconSrc: imageIcon,
      },
      {
        key: 'reel',
        label: 'Reel',
        path: '/generate-media/chat-reel',
        iconSrc: reelIcon,
      },
    ],
  },
];

function renderOptionIcon(iconSrc: string) {
  return (
    <span className="sidebar-create-dropdown-icon">
      <img src={iconSrc} alt="" />
    </span>
  );
}

const SidebarCreateEntry: React.FC<SidebarCreateEntryProps> = ({ collapsed = false }) => {
  const [open, setOpen] = useState(false);
  const [museExpanded, setMuseExpanded] = useState(true);

  const triggerClassName = useMemo(() => {
    const classes = ['sidebar-create-entry-trigger'];
    if (collapsed) {
      classes.push('sidebar-create-entry-trigger-collapsed');
    }
    if (open) {
      classes.push('sidebar-create-entry-trigger-active');
    }
    return classes.join(' ');
  }, [collapsed, open]);

  const handleNavigate = (path: string) => {
    setOpen(false);
    history.push(path);
  };

  const popoverContent = (
    <div className="sidebar-create-dropdown">
      {CREATE_PRODUCT_OPTIONS.map((option) => {
        if (option.children?.length) {
          return (
            <div key={option.key} className="sidebar-create-dropdown-group">
              <button
                type="button"
                className="sidebar-create-dropdown-muse-toggle"
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  setMuseExpanded((previous) => !previous);
                }}
              >
                <span className="sidebar-create-dropdown-muse-main">
                  {renderOptionIcon(option.iconSrc)}
                  <span>{option.label}</span>
                </span>
                <DownOutlined className={`sidebar-create-dropdown-chevron${museExpanded ? ' sidebar-create-dropdown-chevron-open' : ''}`} />
              </button>
              {museExpanded ? (
                <div className="sidebar-create-dropdown-children">
                  {option.children.map((child) => (
                    <button key={child.key} type="button" className="sidebar-create-dropdown-child" onClick={() => handleNavigate(child.path)}>
                      {renderOptionIcon(child.iconSrc)}
                      <span>{child.label}</span>
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
          );
        }

        if (!option.path) {
          return null;
        }

        return (
          <button key={option.key} type="button" className="sidebar-create-dropdown-item" onClick={() => handleNavigate(option.path!)}>
            {renderOptionIcon(option.iconSrc)}
            <span>{option.label}</span>
          </button>
        );
      })}
    </div>
  );

  return (
    <div className={`sidebar-create-entry${collapsed ? ' sidebar-create-entry-collapsed' : ''}`}>
      <Popover open={open} onOpenChange={setOpen} trigger={collapsed ? ['hover'] : ['click']} placement="rightTop" arrow destroyOnHidden overlayClassName="sidebar-create-popover" content={popoverContent}>
        <button type="button" className={triggerClassName} aria-label="新建">
          <span className="sidebar-create-entry-trigger-icon">
            <PlusOutlined />
          </span>
          {collapsed ? null : <span className="sidebar-create-entry-trigger-label">新建</span>}
        </button>
      </Popover>
    </div>
  );
};

export default SidebarCreateEntry;
